/**
 * "Hồ sơ buổi làm" trong tờ chi tiết lịch (N3 — docs/plans/pv2-n3-quay-lai.md §3, §6).
 *
 *  - "Lần trước": ghi chú các buổi trước + ảnh của lần làm xong gần nhất (thợ không phải hỏi lại khách).
 *  - "Ghi chú buổi này" và "Chụp ảnh" chỉ khi khách đã tới / đang làm / xong.
 *  - Chụp ảnh: khách chưa đồng ý → hỏi MỘT câu (nói rõ thời hạn giữ) TRƯỚC khi mở máy ảnh; chưa đồng ý thì
 *    không thu thập ảnh nào (soát R5). Ảnh được thu nhỏ ở máy (cạnh dài 1600px, JPEG) trước khi gửi — máy
 *    chủ vẫn làm sạch EXIF / GPS và mã hoá lại.
 * Máy chủ tự lọc: KTV chỉ thấy lịch có phần việc của mình (404 → mục này im lặng không hiện).
 *
 * Lỗi "lưu ảnh chụp khách không thành công" (docs/soat/loi-luu-anh-khach.md, 30/10/2026): đường lưu không hỏng nhưng mọi báo ("Đã lưu ảnh", lỗi, hướng
 * dẫn sau "Khách đồng ý") từng nằm ở CUỐI thân tờ — ngoài màn — nên thợ không thấy gì và tưởng chưa lưu. Nay mọi báo của khối này nằm NGAY DƯỚI hàng nút
 * (`PhanAnhBuoi`), lỗi `role="alert"`, tự cuộn vào tầm nhìn; lưu xong hiện "Ảnh buổi này: n" + nút xem từng ảnh; bấm đúp không lưu hai ảnh.
 */
import { Camera } from 'lucide-react'
import { useEffect, useRef, useState } from 'react'
import { cauLoiAnhKhach, cauLoiDongYAnh, loiDoiCauHoi, thuNho } from './anh-khach.ts'
import { type Api, ApiFailure, type DayAppointment, type LanTruoc } from './api.ts'
import { ngayViet } from './GoiVi.tsx'
import { KhoiLuuY } from './Khach.tsx'
import { H } from './text/ho-so.ts'
import { T } from './text/vi.ts'
import { ThongBao } from './ui.tsx'

const DA_TOI = ['checked_in', 'in_service', 'done']
/** Hai lần bấm "Chụp ảnh" cách nhau dưới khoảng này là bấm đúp: chỉ mở máy ảnh một lần. */
const KHOANG_CHONG_BAM_DUP_MS = 1000

/** Báo của khối ảnh: lỗi · đã lưu · hướng dẫn sau "Khách đồng ý". `lan` tăng mỗi lần báo để cùng một câu hiện lại vẫn cuộn vào màn. */
export type BaoAnh = { kieu: 'loi' | 'thanh-cong' | 'huong-dan'; cau: string; lan: number }

/** Khối báo: lỗi `role="alert"`; hiện ra thì cuộn vào tầm nhìn của thân tờ (CSS `.pab-bao` chừa lề). */
function KhoiBaoAnh({ bao }: { bao: BaoAnh }) {
  const goc = useRef<HTMLDivElement>(null)
  // biome-ignore lint/correctness/useExhaustiveDependencies: chạy lại đúng mỗi lần có báo mới (`lan`).
  useEffect(() => {
    goc.current?.scrollIntoView({ block: 'nearest' })
  }, [bao.lan])
  return (
    <div className="pab-bao" ref={goc}>
      <ThongBao loai={bao.kieu === 'loi' ? 'loi' : 'thanh-cong'} khan={bao.kieu === 'loi'}>
        {bao.cau}
      </ThongBao>
    </div>
  )
}

/** Nút "Chụp ảnh". `noiBat` = khách vừa đồng ý: đây là việc kế tiếp duy nhất nên là nút chính. */
export function NutChupAnh({
  dang,
  noiBat,
  onChup,
}: {
  dang: boolean
  noiBat: boolean
  onChup: () => void
}) {
  return (
    <button
      type="button"
      className={noiBat ? 'nut nut-chinh' : 'nut nut-vien'}
      disabled={dang}
      onClick={onChup}
    >
      <Camera size={18} />
      {dang ? H.dangTaiAnh : H.chupAnh}
    </button>
  )
}

/**
 * Mọi thứ dưới hàng nút "Lưu ghi chú | Chụp ảnh": hộp hỏi đồng ý, báo (lỗi / đã lưu / hướng dẫn) và bằng chứng "Ảnh buổi này: n" + nút xem.
 * Trình bày thuần (không tự gọi máy chủ) để test vẽ ra chữ / nút được.
 */
export function PhanAnhBuoi({
  hoiDongY,
  hanGiuNgay,
  luuNgoaiVN,
  dang,
  bao,
  anhVua,
  onDongY,
  onKhongChup,
  onXemAnh,
}: {
  hoiDongY: boolean
  hanGiuNgay: number
  /** Kho ảnh khách của máy chủ ở nước ngoài (R2): hộp hỏi phải nói thẳng (NĐ13, soát R2 N1). */
  luuNgoaiVN: boolean
  dang: boolean
  bao: BaoAnh | null
  /** Ảnh đã lưu THÀNH CÔNG trong lần mở tờ này (id máy chủ trả) — bằng chứng cho thợ. */
  anhVua: { id: string }[]
  onDongY: () => void
  onKhongChup: () => void
  onXemAnh: (id: string) => void
}) {
  if (!hoiDongY && bao === null && anhVua.length === 0) return null
  return (
    <>
      {hoiDongY && (
        <div className="hoi-dong-y" role="alertdialog" aria-label={H.hoiDongY}>
          <b>{H.hoiDongY}</b>
          <p className="chu-phu">
            {H.hoiDongYPhu(hanGiuNgay)}
            {luuNgoaiVN ? ` ${H.luuNgoaiVN}` : ''}
          </p>
          <div className="hang-phu">
            <button type="button" className="nut nut-chinh" disabled={dang} onClick={onDongY}>
              {H.khachDongY}
            </button>
            <button type="button" className="nut nut-nhat" onClick={onKhongChup}>
              {H.khongChup}
            </button>
          </div>
        </div>
      )}
      {bao !== null && <KhoiBaoAnh key={bao.lan} bao={bao} />}
      {anhVua.length > 0 && (
        <div className="anh-buoi-nay">
          <b className="nhan">{H.anhBuoiNay(anhVua.length)}</b>
          <div className="hang-phu">
            {anhVua.map((a, i) => (
              <button
                key={a.id}
                type="button"
                className="nut nut-nhat"
                aria-label={H.xemAnhBuoiNay(i + 1)}
                onClick={() => onXemAnh(a.id)}
              >
                {H.xemAnh(i + 1)}
              </button>
            ))}
          </div>
        </div>
      )}
    </>
  )
}

export function HoSoBuoi({
  api,
  lich,
  coAnh,
}: {
  api: Api
  lich: DayAppointment
  /** Người xem có quyền ảnh khách (`xem.anh-khach`). */
  coAnh: boolean
}) {
  const [lt, setLt] = useState<LanTruoc | null>(null)
  const [ghiChu, setGhiChu] = useState('')
  const [dang, setDang] = useState(false)
  const [hoiDongY, setHoiDongY] = useState(false)
  // Khách vừa đồng ý mà chưa lưu ảnh nào: "Chụp ảnh" là nút chính, câu hướng dẫn nằm ngay dưới hàng nút.
  const [huongDan, setHuongDan] = useState(false)
  const [bao, setBao] = useState<BaoAnh | null>(null)
  const [anhVua, setAnhVua] = useState<{ id: string }[]>([])
  const oTep = useRef<HTMLInputElement>(null)
  // Khoá chống lặp ĐỒNG BỘ (state chỉ đổi ở lần vẽ kế, hai lần chạm sát nhau cùng thấy "chưa bận").
  const dangGui = useRef(false)
  const lanMoCuoi = useRef(Number.NEGATIVE_INFINITY)
  const lanBao = useRef(0)
  const daToi = DA_TOI.includes(lich.status)
  const customerId = lich.customerId ?? null

  useEffect(() => {
    let con = true
    void api
      .lanTruoc(lich.appointmentId)
      .then((r) => {
        if (con) setLt(r)
      })
      // Không phải lịch của mình / lịch chưa có khách → không có mục này, không báo lỗi.
      .catch(() => undefined)
    return () => {
      con = false
    }
  }, [api, lich.appointmentId])

  function datBao(kieu: BaoAnh['kieu'], cau: string) {
    lanBao.current += 1
    setBao({ kieu, cau, lan: lanBao.current })
  }

  function loiChung(e: unknown) {
    datBao('loi', e instanceof ApiFailure ? e.messageVi : T.serverFailed)
  }

  async function luuGhiChu() {
    setDang(true)
    try {
      await api.ghiChuBuoi(lich.appointmentId, ghiChu.trim())
      setGhiChu('')
      datBao('thanh-cong', H.daLuuGhiChu)
    } catch (e) {
      loiChung(e)
    } finally {
      setDang(false)
    }
  }

  async function chonTep(tep: File | undefined) {
    if (tep === undefined || customerId === null) return
    if (dangGui.current) return
    dangGui.current = true
    // Khoá nút NGAY (trước khi thu nhỏ ảnh 12–48 MP, có thể mất vài giây): trước đây nút chỉ khoá sau bước đó nên bấm tiếp được.
    setDang(true)
    setBao(null)
    try {
      const base64 = await thuNho(tep)
      const r = await api.taiAnh({
        customerId,
        appointmentId: lich.appointmentId,
        kind: 'after',
        dataBase64: base64,
      })
      setAnhVua((cu) => [...cu, { id: r.id }])
      setHuongDan(false)
      datBao('thanh-cong', H.daLuuAnh)
    } catch (e) {
      datBao('loi', cauLoiAnhKhach(e))
    } finally {
      dangGui.current = false
      setDang(false)
    }
  }

  /** Bấm "Chụp ảnh": chưa có đồng ý thì hỏi trước — máy ảnh chưa mở, chưa có ảnh nào. Bấm đúp / đang gửi thì bỏ qua. */
  function chup() {
    const bay = performance.now()
    if (dangGui.current || bay - lanMoCuoi.current < KHOANG_CHONG_BAM_DUP_MS) return
    if (lt?.daDongYAnh === true) {
      // Chỉ lần MỞ MÁY ẢNH mới tính mốc chống bấm đúp (mở hộp hỏi đồng ý thì bấm lại vô hại).
      lanMoCuoi.current = bay
      oTep.current?.click()
    } else setHoiDongY(true)
  }

  async function dongY() {
    if (customerId === null) return
    setDang(true)
    try {
      // Gửi kèm ĐÚNG câu đang hiện (cùng nguồn với `PhanAnhBuoi` bên dưới): máy chủ lệch với cấu hình hiện tại thì từ chối (V3).
      await api.dongYAnh(customerId, true, {
        hanGiuNgay: lt?.hanGiuNgay ?? 30,
        luuNgoaiVN: lt?.luuNgoaiVN === true,
      })
      setLt((cu) => (cu === null ? cu : { ...cu, daDongYAnh: true }))
      setHoiDongY(false)
      // Không mở máy ảnh hộ (Safari chỉ cho mở trong đúng lần chạm): nút "Chụp ảnh" thành nút chính + câu hướng dẫn ngay dưới hàng nút.
      setHuongDan(true)
      datBao('huong-dan', H.huongDanSauDongY)
    } catch (e) {
      if (loiDoiCauHoi(e)) {
        // Cách xử lý ảnh đổi giữa lúc hiện câu và lúc bấm: tải lại để hộp hỏi hiện số mới (hộp vẫn mở), báo rõ — không im lặng.
        await api
          .lanTruoc(lich.appointmentId)
          .then((r) => setLt(r))
          .catch(() => undefined)
      }
      datBao('loi', cauLoiDongYAnh(e))
    } finally {
      setDang(false)
    }
  }

  async function xemAnh(id: string) {
    // Mở cửa sổ NGAY trong lần bấm (trình duyệt chặn popup mở sau một `await`); cắt `opener` để trang
    // kho ảnh không điều hướng ngược được tab quầy.
    const cua = window.open('about:blank', '_blank')
    if (cua !== null) cua.opener = null
    try {
      const r = await api.urlAnh(id)
      if (cua !== null) cua.location.href = r.url
    } catch (e) {
      cua?.close()
      loiChung(e)
    }
  }

  const coLanTruoc = lt !== null && (lt.ghiChu.length > 0 || (coAnh && lt.anh.length > 0))
  // Không có gì để xem và chưa tới lúc ghi / chụp → không vẽ khung rỗng.
  const coLuuY = (lt?.luuY ?? null) !== null
  if (!coLanTruoc && !(daToi && customerId !== null) && lt?.choXacNhanZalo !== true && !coLuuY) {
    return null
  }

  return (
    <div className="ho-so-buoi">
      {/* "Lưu ý cố định" của khách (STT 2.1): đứng đầu, trên "Lần trước" — điều phải nhớ mỗi lần khách tới. */}
      <KhoiLuuY luuY={lt?.luuY} />
      {lt?.choXacNhanZalo === true && <p className="chu-phu">{H.choXacNhanZalo}</p>}
      {lt !== null && coLanTruoc && (
        <div className="lan-truoc">
          <b className="nhan">
            {H.lanTruoc(ngayViet(lt.ngayLanTruoc ?? lt.ghiChu[0]?.ngay ?? ''))}
          </b>
          {lt.ghiChu.map((g) => (
            <p key={`${g.ngay}-${g.noiDung}`}>
              {g.noiDung}
              <span className="chu-phu">
                {/* Ngày chỉ ghi khi khác ngày ở tiêu đề — không lặp hai lần (ui-critic v21). */}
                {g.ngay !== (lt.ngayLanTruoc ?? lt.ghiChu[0]?.ngay) && ` · ${ngayViet(g.ngay)}`}
                {g.nguoi !== null && ` · ${g.nguoi}`}
              </span>
            </p>
          ))}
          {coAnh && lt.anh.length > 0 && (
            <div className="hang-phu">
              {lt.anh.map((a, i) => (
                <button
                  key={a.id}
                  type="button"
                  className="nut nut-nhat"
                  onClick={() => void xemAnh(a.id)}
                >
                  {H.xemAnh(i + 1)}
                </button>
              ))}
            </div>
          )}
        </div>
      )}

      {daToi && customerId !== null && (
        <div className="cot-nhap">
          <label className="nhan" htmlFor={`ghi-chu-${lich.appointmentId}`}>
            {H.ghiChuNhan}
          </label>
          <textarea
            id={`ghi-chu-${lich.appointmentId}`}
            className="o-nhap"
            rows={2}
            maxLength={500}
            placeholder={H.ghiChuGoiY}
            value={ghiChu}
            onChange={(e) => setGhiChu(e.target.value)}
          />
          <div className="hang-phu">
            <button
              type="button"
              className="nut nut-vien"
              disabled={dang || ghiChu.trim() === ''}
              onClick={() => void luuGhiChu()}
            >
              {H.luuGhiChu}
            </button>
            {coAnh && lt !== null && <NutChupAnh dang={dang} noiBat={huongDan} onChup={chup} />}
          </div>
          {coAnh && (
            <input
              ref={oTep}
              type="file"
              accept="image/*"
              capture="environment"
              hidden
              onChange={(e) => {
                void chonTep(e.target.files?.[0])
                e.target.value = ''
              }}
            />
          )}
          <PhanAnhBuoi
            hoiDongY={hoiDongY && coAnh && lt !== null}
            hanGiuNgay={lt?.hanGiuNgay ?? 30}
            luuNgoaiVN={lt?.luuNgoaiVN === true}
            dang={dang}
            bao={bao}
            anhVua={anhVua}
            onDongY={() => void dongY()}
            onKhongChup={() => setHoiDongY(false)}
            onXemAnh={(id) => void xemAnh(id)}
          />
        </div>
      )}
    </div>
  )
}
